import { AdminClient } from '@storefronts/common-types'
import { NextRouter } from 'next/router'

/**
 * Reads clientHash from Next.js router query.
 */
export function getClientHashFromQuery(
    query: NextRouter['query'],
): string | undefined {
    const raw = query.clientHash
    return typeof raw === 'string' ? raw : undefined
}

export type ProcessClientHashFromUrlParams = {
    query: NextRouter['query']
    pathname: string
    replace: NextRouter['replace']
    allClients: AdminClient[]
    currentClient: AdminClient
}

export type ProcessClientHashFromUrlResult = {
    client: AdminClient
    accessDenied: boolean
}

/**
 * Handles clientHash in the URL (e.g. Shopify app install redirect).
 * If present: resolves client from allClients by permission; clears the query param.
 * Returns the client to use and whether access was denied (caller should show error toast).
 */
export function processClientHashFromUrl({
    query,
    pathname,
    replace,
    allClients,
    currentClient,
}: ProcessClientHashFromUrlParams): ProcessClientHashFromUrlResult {
    const clientHashFromQuery = getClientHashFromQuery(query)

    if (!clientHashFromQuery) {
        return { client: currentClient, accessDenied: false }
    }

    const clientFromQuery = allClients.find(
        (c) => c.clientHash === clientHashFromQuery,
    )

    // Clear clientHash from URL after processing
    const { clientHash: _omit, ...restQuery } = query
    replace({ pathname, query: restQuery }, undefined, { shallow: true })

    if (clientFromQuery) {
        return { client: clientFromQuery, accessDenied: false }
    }

    return { client: currentClient, accessDenied: true }
}
